/**
 * Each editor of a mission in a colour of their own, so two people in the designer at once tell
 * at a glance who is where and who changed what (roadmap item `edicio-a-diverses-mans`). The
 * colours are the `--editor-1` to `--editor-8` tokens of `styles/design.css`; amber is not one of
 * them, because amber is the page's own accent (the open panel, the active tool).
 *
 * Every browser in the room works the slots out by itself from the same names (the presence the
 * platform broadcasts and the history's authors), so they agree on them without the platform
 * choosing: each name prefers the slot its hash points to and, in alphabetical order, takes the
 * next free one when an earlier name has it. Two editors never share a colour while there are no
 * more than eight names; past eight, colours repeat.
 */
export const EDITOR_SLOTS = 8;

function hashOf(handle: string): number {
  let h = 0;
  for (const c of handle) h = (Math.imul(h, 31) + c.codePointAt(0)!) >>> 0;
  return h;
}

/** Each handle's slot, from 1 to {@link EDITOR_SLOTS}. Empty handles have none. */
export function editorSlots(handles: Iterable<string | null | undefined>): Map<string, number> {
  const names = [...new Set([...handles].filter((h): h is string => !!h))].sort();
  const taken = new Set<number>();
  const out = new Map<string, number>();
  for (const name of names) {
    let slot = hashOf(name) % EDITOR_SLOTS;
    if (taken.size < EDITOR_SLOTS) {
      while (taken.has(slot)) slot = (slot + 1) % EDITOR_SLOTS;
    }
    taken.add(slot);
    out.set(name, slot + 1);
  }
  return out;
}

/** The CSS colour of a slot, for a `style` that sets `--editor` on the element that wears it. */
export const editorColour = (slot: number | undefined): string =>
  slot ? `var(--editor-${slot})` : 'var(--muted)';

/**
 * The panel of the designer a patch path belongs to: its first segment, when a panel edits it.
 * The title and the summary are no panel's: their card sits above every panel.
 */
const PANEL_OF_ROOT: Record<string, string> = {
  objectives: 'objectives',
  script: 'script',
  area: 'area',
  markers: 'markers',
  forces: 'forces',
  compositions: 'compositions',
  civilians: 'civilians',
  intel: 'intel',
  twists: 'twists',
  briefing: 'briefing',
  cast: 'cast',
  story: 'story',
  factions: 'factions',
  loadouts: 'loadouts',
  supports: 'supports',
  rules: 'rules',
};

export function panelOfPath(path: string): string | null {
  const root = path.split('/')[1] ?? '';
  return PANEL_OF_ROOT[root] ?? null;
}

/** The field above the panels a patch path is, when it is one: the title or the summary. */
export function fieldOfPath(path: string): 'title' | 'summary' | null {
  return path === '/title' ? 'title' : path === '/summary' ? 'summary' : null;
}

/**
 * The panel an editor's focus names. The designer sends a panel's key when a tab is opened, and
 * a field's own name (the title, the summary, an objective's id) when a field takes the focus;
 * the title and the summary are fields above the panels, so they name none.
 */
export function panelOfFocus(
  focus: string,
  panels: readonly string[],
  objectiveIds: readonly string[],
): string | null {
  if (!focus) return null;
  if (panels.includes(focus)) return focus;
  if (objectiveIds.includes(focus)) return 'objectives';
  return null;
}
